import { useCallback, useId, useRef, type PointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { googleMapsUrl } from '@shared/maps';
import { formatDistance, categoryLabel } from '@/lib/formatting';
import { HeartIcon } from './Icons';
import type { Place } from '@shared/types';
import type { PlaceWithState } from '@/types/api';

export type SheetSnap = 'collapsed' | 'peek' | 'expanded';

const SNAP_ORDER: SheetSnap[] = ['collapsed', 'peek', 'expanded'];

interface RanchMapSheetProps {
  snap: SheetSnap;
  onSnap: (snap: SheetSnap) => void;
  places: PlaceWithState[] | Place[];
  selected: PlaceWithState | Place | null;
  onSelect: (place: PlaceWithState | Place) => void;
  isFavorite?: (id: number) => boolean;
  onToggleFavorite?: (id: number) => void;
}

function nextSnap(current: SheetSnap, direction: 1 | -1): SheetSnap {
  const i = SNAP_ORDER.indexOf(current);
  return SNAP_ORDER[Math.max(0, Math.min(SNAP_ORDER.length - 1, i + direction))];
}

export function RanchMapSheet({
  snap,
  onSnap,
  places,
  selected,
  onSelect,
  isFavorite,
  onToggleFavorite,
}: RanchMapSheetProps) {
  const titleId = useId();
  const dragRef = useRef<{ y: number; snap: SheetSnap } | null>(null);
  const count = places.length;
  const countLabel = count === 1 ? '1 place' : `${count} places`;

  const onHandlePointerDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      dragRef.current = { y: e.clientY, snap };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [snap],
  );

  const onHandlePointerUp = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      const start = dragRef.current;
      dragRef.current = null;
      if (!start) return;
      const dy = start.y - e.clientY;
      if (Math.abs(dy) < 24) return;
      onSnap(nextSnap(start.snap, dy > 0 ? 1 : -1));
    },
    [onSnap],
  );

  const toggleExpanded = () => onSnap(snap === 'expanded' ? (selected ? 'peek' : 'collapsed') : 'expanded');

  const heading = selected && snap !== 'expanded' ? selected.name : `Nearby · ${countLabel}`;

  return (
    <section
      className={`ranch-map-sheet ranch-map-sheet--${snap}`}
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="ranch-map-sheet__handle"
        aria-label={snap === 'expanded' ? 'Collapse nearby list' : 'Expand nearby list'}
        aria-expanded={snap === 'expanded'}
        onClick={toggleExpanded}
        onPointerDown={onHandlePointerDown}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
      >
        <span className="ranch-map-sheet__grip" />
      </button>

      <button type="button" className="ranch-map-sheet__title" id={titleId} onClick={toggleExpanded}>
        {heading}
      </button>

      {selected && snap !== 'expanded' && (
        <RanchMapPlaceCard
          place={selected}
          isFavorite={isFavorite?.(selected.id)}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {snap === 'expanded' && (
        <ul className="ranch-map-sheet__list">
          {places.length === 0 && (
            <li className="ranch-map-sheet__empty">No places in this area.</li>
          )}
          {places.map((p) => {
            const active = selected?.id === p.id;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  className={`ranch-map-sheet__row ${active ? 'ranch-map-sheet__row--on' : ''}`}
                  onClick={() => onSelect(p)}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="ranch-map-sheet__row-name">{p.name}</span>
                  <span className="ranch-map-sheet__row-meta">
                    {categoryLabel(p.category)}
                    {p.distance_from_home_miles != null && ` · ${formatDistance(p.distance_from_home_miles)}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function RanchMapPlaceCard({
  place,
  isFavorite,
  onToggleFavorite,
}: {
  place: PlaceWithState | Place;
  isFavorite?: boolean;
  onToggleFavorite?: (id: number) => void;
}) {
  const miles = formatDistance(place.distance_from_home_miles);
  return (
    <div className="ranch-map-card">
      <p className="ranch-map-card__meta">
        {categoryLabel(place.category)}
        {miles ? ` · ${miles}` : ''}
      </p>
      <div className="ranch-map-card__actions">
        <a
          className="btn btn-primary btn-sm"
          href={googleMapsUrl(place.address)}
          target="_blank"
          rel="noreferrer"
        >
          Directions
        </a>
        <Link className="btn btn-secondary btn-sm" to={`/places/${place.slug}`} state={{ from: 'map' }}>
          Open details
        </Link>
        {onToggleFavorite && (
          <button
            type="button"
            className="btn btn-ghost btn-sm ranch-map-card__save"
            aria-pressed={!!isFavorite}
            aria-label={isFavorite ? `Remove ${place.name} from Family Favorites` : `Save ${place.name} to Family Favorites`}
            onClick={() => onToggleFavorite(place.id)}
          >
            <HeartIcon filled={!!isFavorite} />
            Save
          </button>
        )}
      </div>
    </div>
  );
}
